import React, { useMemo, useState } from 'react';
import { Award, EyeOff, Plus, RotateCcw, Trash2 } from 'lucide-react';
import {
  newSkillId,
  type ProgramSkillOption,
  type ProgramSkillsConfig,
} from '../../../lib/programSkills';

/**
 * Admin editor for ONE program's Skills & Abilities list + selection rule.
 *
 * - Add / rename skills inline; ids are generated once and never change,
 *   so applicant selections stay linked across renames.
 * - "Remove" DEACTIVATES a skill (active: false) instead of deleting it:
 *   applicants who already selected it keep their stored selection and
 *   its label snapshot; the skill only disappears from new selections,
 *   applicant pickers and employer filter options. Reactivate restores it.
 * - A hard delete is offered ONLY for a skill that is already inactive
 *   (it can no longer be selected; stored selections keep their label
 *   snapshot either way).
 * - Selection rule: Single, or Multiple with an optional maximum —
 *   leaving the maximum blank means unlimited.
 */
export default function ProgramSkillsEditor({
  config,
  onChange,
}: {
  config: ProgramSkillsConfig;
  onChange: (config: ProgramSkillsConfig) => void;
}) {
  const [newLabel, setNewLabel] = useState('');

  const duplicateLabels = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of config.skills) {
      const key = s.label.trim().toLowerCase();
      if (key) counts.set(key, (counts.get(key) || 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([k]) => k));
  }, [config.skills]);

  const setSkills = (skills: ProgramSkillOption[]) => onChange({ ...config, skills });
  const updateSkill = (id: string, changes: Partial<ProgramSkillOption>) =>
    setSkills(config.skills.map((s) => (s.id === id ? { ...s, ...changes } : s)));

  const addSkill = () => {
    const label = newLabel.trim();
    if (!label) return;
    setSkills([...config.skills, { id: newSkillId(), label, active: true }]);
    setNewLabel('');
  };

  const hardDelete = (id: string) => setSkills(config.skills.filter((s) => s.id !== id));

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Award className="w-4 h-4 text-blue-600" /> Skills &amp; Abilities
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          The predefined skills applicants for THIS program can select in their Application
          Preferences. The list belongs to this program only — it never appears on another
          program unless you configure it there too. When the list has at least one active
          skill, selecting skills is mandatory before an applicant can submit.
        </p>
      </div>

      {/* Selection rule */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Selection rule</p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Selection rule">
          <button
            type="button"
            role="radio"
            aria-checked={config.selectionMode === 'single'}
            onClick={() => onChange({ ...config, selectionMode: 'single', maxSelections: 1 })}
            className={`px-3 py-2 rounded-lg text-xs font-bold border cursor-pointer ${config.selectionMode === 'single' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'}`}
          >
            Single selection
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={config.selectionMode === 'multiple'}
            onClick={() => onChange({ ...config, selectionMode: 'multiple', maxSelections: config.maxSelections === 1 ? null : config.maxSelections })}
            className={`px-3 py-2 rounded-lg text-xs font-bold border cursor-pointer ${config.selectionMode === 'multiple' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'}`}
          >
            Multiple selection
          </button>
        </div>
        {config.selectionMode === 'multiple' && (
          <div className="flex items-center gap-3">
            <label htmlFor="skills-max" className="text-xs font-semibold text-slate-600">
              Maximum selections
            </label>
            <input
              id="skills-max"
              type="number"
              min={1}
              step={1}
              value={config.maxSelections == null ? '' : String(config.maxSelections)}
              onChange={(e) => {
                const raw = e.target.value.trim();
                const n = raw === '' ? null : Math.max(1, Math.floor(Number(raw)) || 1);
                onChange({ ...config, maxSelections: raw === '' ? null : n });
              }}
              placeholder="No limit"
              className="w-28 bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500"
            />
            <span className="text-xs text-slate-500">Leave blank for unlimited selections.</span>
          </div>
        )}
      </div>

      {/* Skill rows */}
      <div className="space-y-2">
        {config.skills.length === 0 && (
          <p className="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-xl p-4">
            No skills configured yet. Add the first skill below — applicants will not see a
            skills field (and will not be blocked) until at least one active skill exists.
          </p>
        )}
        {config.skills.map((skill) => {
          const isDup = duplicateLabels.has(skill.label.trim().toLowerCase());
          return (
            <div
              key={skill.id}
              className={`flex items-center gap-2 rounded-xl border p-2.5 ${skill.active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200'}`}
            >
              <input
                aria-label="Skill label"
                value={skill.label}
                disabled={!skill.active}
                onChange={(e) => updateSkill(skill.id, { label: e.target.value })}
                className={`flex-1 min-w-0 bg-white border rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 ${isDup ? 'border-rose-400' : 'border-slate-300'} ${skill.active ? 'text-slate-800' : 'text-slate-400'}`}
              />
              <span
                className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full ${skill.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}
              >
                {skill.active ? 'Active' : 'Inactive'}
              </span>
              {skill.active ? (
                <button
                  type="button"
                  onClick={() => updateSkill(skill.id, { active: false })}
                  title="Remove from selection (deactivate — existing applicant selections are kept)"
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 rounded-lg cursor-pointer"
                >
                  <EyeOff className="w-3.5 h-3.5" /> Remove
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => updateSkill(skill.id, { active: true })}
                    title="Reactivate this skill"
                    className="shrink-0 inline-flex items-center gap-1 px-2.5 py-2 text-xs font-bold text-blue-600 hover:bg-blue-50 rounded-lg cursor-pointer"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Reactivate
                  </button>
                  <button
                    type="button"
                    onClick={() => hardDelete(skill.id)}
                    title="Delete permanently (stored applicant selections keep their label snapshot)"
                    className="shrink-0 inline-flex items-center gap-1 px-2.5 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </>
              )}
            </div>
          );
        })}
        {duplicateLabels.size > 0 && (
          <p className="text-xs text-rose-600 font-semibold">Two skills share the same label — rename one before saving.</p>
        )}
      </div>

      {/* Add */}
      <div className="flex items-center gap-2">
        <input
          aria-label="New skill label"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
          placeholder="e.g. Customer Service"
          className="flex-1 min-w-0 bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-blue-500"
        />
        <button
          type="button"
          onClick={addSkill}
          disabled={!newLabel.trim()}
          className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg cursor-pointer"
        >
          <Plus className="w-4 h-4" /> Add skill
        </button>
      </div>
    </div>
  );
}
